import { FileDownload as FileDownloadIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  downloadIftaReport,
  IftaReportGeneration,
  useGetIftaGenerationsQuery,
} from "../../api/reportsApi";
import { DataTable } from "../../components/common/DataTable/DataTable";
import {
  getIftaReportStatusChipColor,
  getIftaReportStatusLabel,
} from "../../utils/statusColors";

interface IftaGenerationsListProps {
  token: string;
  showHeading?: boolean;
}

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const IftaGenerationsList: React.FC<IftaGenerationsListProps> = ({
  token,
  showHeading = true,
}) => {
  const { data: generations = [], isLoading, isError, refetch } = useGetIftaGenerationsQuery();
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (row: IftaReportGeneration) => {
    if (row.status !== "completed") return;
    setDownloadingJobId(row.jobId);
    setDownloadError(null);
    try {
      await downloadIftaReport(row.jobId, row.quarter, token);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const columns = [
    { field: "quarter", headerName: "Quarter", flex: 1 },
    {
      field: "createdAt",
      headerName: "Generated",
      flex: 1.2,
      renderCell: (value: string) => formatDate(value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (value: string) => (
        <Chip
          label={getIftaReportStatusLabel(value)}
          size="small"
          color={getIftaReportStatusChipColor(value)}
          variant="outlined"
        />
      ),
    },
    {
      field: "wasFree",
      headerName: "Billing",
      flex: 0.8,
      renderCell: (value: boolean) => (value ? "Free" : "Paid"),
    },
    {
      field: "userName",
      headerName: "Generated By",
      flex: 1.2,
      renderCell: (value: string | null | undefined) => value || "—",
    },
  ];

  return (
    <Box>
      {showHeading && (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "text.primary", fontWeight: 600, mb: 1 }}
          >
            Generated Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Previously generated IFTA reports for your company. Download completed reports as PDF.
          </Typography>
        </>
      )}

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {isError ? (
        <Alert severity="error">
          Failed to load report history.{" "}
          <Box component="span" sx={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => refetch()}>
            Retry
          </Box>
        </Alert>
      ) : (
        <DataTable<IftaReportGeneration>
          items={generations}
          columns={columns}
          isLoading={isLoading}
          title=""
          getRowId={(row) => row.jobId}
          actions={(row) => [
            {
              icon: downloadingJobId === row.jobId ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon fontSize="small" />
              ),
              label: "Download PDF",
              onClick: () => handleDownload(row),
              disabled: row.status !== "completed" || downloadingJobId != null,
              color: "primary",
            },
          ]}
        />
      )}
    </Box>
  );
};

export default IftaGenerationsList;
